import type { Client, StompSubscription } from '@stomp/stompjs';
import produce from 'immer';
import useObservable from 'packages/workspace/modules/helpers/hooks/useObservable';
import type { EventFormat } from 'packages/workspace/services/models/global-models';

import { BehaviorSubject } from 'rxjs';
import type { IChatMessage } from '../../models/chat-message';
import type { ChatConversationMessage, InternalChatConversation } from '../../models/internal-channel';
import { MessageTopicsConstants } from './chat-store';
import { SoundOptions } from '../useSoundStore';

export interface IActiveConnection {
  id: string;
  connection: StompSubscription;
  conversation: InternalChatConversation;
}

export interface InternalChatState {
  active_conversations: InternalChatConversation[];
  ongoing_messages: Record<string, IChatMessage[]>;
  client: Client;
  active_window: InternalChatConversation | undefined;
  autoScrollEnabled: boolean;
  total_messages: number;
}

export const initialInternalConversationState: InternalChatState = {
  client: null as unknown as Client,
  active_conversations: [],
  ongoing_messages: {},
  active_window: undefined,
  autoScrollEnabled: true,
  total_messages: 0,
};

const internalConversation$ = new BehaviorSubject(initialInternalConversationState);

export const InternalConversationStore = {
  /**
   *!Method called For Setting Websocket Client
   * @param client
   */
  setClient: (client: Client): void => {
    const state = produce(internalConversation$.value, (draftState) => {
      draftState.client = client;
    });
    internalConversation$.next(state);
  },
  /**
   *!Update stomp GROUP CHAT subscriptions
   *!I/p : Active group chats
   * @param active_conversations
   * @param user_id
   */
  updateActiveConversations: (active_conversations: InternalChatConversation[], user_id: string): void => {
    const state = produce(internalConversation$.value, (draftState) => {
      if (!draftState.active_conversations) {
        draftState.active_conversations = [];
      }
      active_conversations.forEach((iActiveConversation) => {
        const iStateActiveConversation = draftState.active_conversations.find((c) => c.conversation_id === iActiveConversation.conversation_id);
        if (iStateActiveConversation) {
          iStateActiveConversation.connection$ = null;
        }
        if (draftState.client.connected) {
          draftState.active_conversations.push(
            produce(iActiveConversation, (draftConversation) => {
              draftConversation.connection$ = draftState.client?.subscribe(`/topic${MessageTopicsConstants.INTERNAL_CHAT}/${iActiveConversation.conversation_id}`, (message) => {
                InternalConversationStore.updateConversationMessages(JSON.parse(message.body), iActiveConversation.conversation_id, user_id);
              });
            })
          );
        }
      });
    });
    internalConversation$.next(state);
  },
  /**
   *!Update Group Chat
   * @param message
   * @param conversation_id
   * @param user_id
   */
  updateConversationMessages: (message: EventFormat<IChatMessage>, conversation_id: string, user_id: string): void => {
    const state = produce(internalConversation$.value, (draftState) => {
      const iActiveConversation = draftState.active_conversations.find((c) => c.conversation_id === message.data.conversationId);
      const active = draftState.active_window?.conversation_id === conversation_id
      if (iActiveConversation) {
        iActiveConversation.last_message = message.data.components.message;
      }
      if (!draftState.ongoing_messages[conversation_id]) {
        draftState.ongoing_messages[conversation_id] = [];
      }
      draftState.ongoing_messages[conversation_id].push(
        produce(message.data, (draftMessage) => {
          draftMessage.isread = draftState.active_window?.conversation_id === conversation_id;
        })
        );
        if (active && message.data.sender !== user_id) {
          if (conversation_id) {
            const conversationMessage = {
              ...message.data,
              event: "SEEN_MESSAGE",
              is_read: true,
              timestamp: Date.now()
            };
            draftState.client.publish({
              destination: `/app${MessageTopicsConstants.INTERNAL_CHAT}/${conversation_id}`,
              body: JSON.stringify(conversationMessage),
            });
          }
        }
        if (!iActiveConversation && !active && message.data.sender !== user_id) {
        SoundOptions.setType(`Internal_Chat_${message.data.id}`)
        draftState.total_messages = draftState.total_messages + 1;
      }
    });
    internalConversation$.next(state);
  },
  /**
   *!Make conversation Active, highlighted
   * @param conversation_id
   */
  setActiveWindow: (conversation_id: string): void => {
    const state = produce(internalConversation$.value, (draftState) => {
      draftState.active_window = draftState.active_conversations.find((eachConversation) => eachConversation.conversation_id === conversation_id);
      draftState.ongoing_messages[conversation_id]?.forEach((m) => (m.isread = true));
    });
    internalConversation$.next(state);
  },
  /**
   *!Used to check if already connected
   * @param conversation_id
   */
  checkIfAlreadyConnected: (conversation_id: string): InternalChatConversation | undefined => {
    return internalConversation$.value.active_conversations.find((connection) => connection.conversation_id === conversation_id);
  },
  /**
   *!Used for incoming new One on One requests.
   * @param newConnection
   */
  updateConnections: (newConnection: IActiveConnection): void => {
    const state = produce(internalConversation$.value, (draftState) => {
      if (!draftState.active_conversations) {
        draftState.active_conversations = [];
      }
      draftState.active_conversations.push(newConnection.conversation);
    });
    internalConversation$.next(state);
  },

  updateBulkMessages: (conversation_id: string, messages: IChatMessage[] = []): void => {
    const state = produce(internalConversation$.value, (draftState) => {
      draftState.ongoing_messages[conversation_id] = messages;
    });
    internalConversation$.next(state);
  },
  /**
   * Updating the ongoing_messages whenever scrolled up for past messages
   * @param conversation_id 
   * @param messages 
   */
  updateInfiniteBulkMessages: (conversation_id: string, messages: IChatMessage[] = []): void => {
    const state = produce(internalConversation$.value, (draftState) => {
      let existingMessages = draftState.ongoing_messages[conversation_id] || []
      existingMessages.unshift(...messages);
      draftState.ongoing_messages[conversation_id] = existingMessages;
    });
    internalConversation$.next(state);
  },

  /**
   *! Clear Store, Used in Logout
   */
  clearStore: (): void => {
    const state = produce(internalConversation$.value, (draftState) => {
      draftState.client?.deactivate();
      return initialInternalConversationState;
    });
    internalConversation$.next(state);
  },

  clearActiveConnections: (): void => {
    const state = produce(internalConversation$.value, (draftState) => {
      draftState.client?.deactivate();
      draftState.active_conversations = [];
    });
    internalConversation$.next(state);
  },
  /**
   *Add One on One  Connection.
   * @param conversation_id
   * @param user_id
   * @param display_name
   */
  addOneOnOneConnection: (conversation_id: string, user_id: string, display_name: string): void => {
    const isActive = internalConversation$.value.active_conversations.find((eachConversation) => eachConversation.conversation_id === conversation_id);

    if (!isActive) {
      const state = produce(internalConversation$.value, (draftState) => {
        if (!draftState.active_conversations) {
          draftState.active_conversations = [];
        }
        draftState.active_conversations.push({
          conversation_id,
          components: {
            type: 'onetoone',
          },
          display_name,
          connection$: draftState.client?.subscribe(`/topic${MessageTopicsConstants.INTERNAL_CHAT}/${conversation_id}`, (message) => {
            InternalConversationStore.updateConversationMessages(JSON.parse(message.body), conversation_id, user_id);
          }),
        });
      });
      internalConversation$.next(state);
    }
  },
  initializeSupervisorsOnlyChat: (conversation_id: string): void => {
    const isActive = internalConversation$.value.active_conversations.find((eachConversation) => eachConversation.conversation_id === conversation_id);

    if (!isActive) {
      const state = produce(internalConversation$.value, (draftState) => {
        if (!draftState.active_conversations) {
          draftState.active_conversations = [];
        }
        draftState.active_conversations.push({
          conversation_id,
          components: {
            type: 'supervisors-only',
          },
          connection$: draftState.client?.subscribe(`/topic${MessageTopicsConstants.SUPERVISORS_ONLY_CHAT}/${conversation_id}`, (message) => {
            InternalConversationStore.updateConversationMessages(JSON.parse(message.body), conversation_id);
          }),
        });
      });
      internalConversation$.next(state);
    }
  },
  addOlderMessagesToConversation: (conversation_id: string, messages: IChatMessage[] = []): void => {
    const state = produce(internalConversation$.value, (draftState) => {
      draftState.ongoing_messages[conversation_id].unshift(
        ...produce(messages, (draftMessages) => {
          draftMessages.reverse();
        })
      );
    });
    internalConversation$.next(state);
  },
  disableAutoScroll: () => {
    const state = produce(internalConversation$.value, (draftState) => {
      draftState.autoScrollEnabled = false;
    });
    internalConversation$.next(state);
  },
  enableAutoScroll: () => {
    const state = produce(internalConversation$.value, (draftState) => {
      draftState.autoScrollEnabled = true;
    });
    internalConversation$.next(state);
  },
  updateUnreadMessagesToZero: (messageCount: number, reduce: boolean) => {
    const state = produce(internalConversation$.value, (draftState) => {
      if (reduce) {
        draftState.total_messages = draftState.total_messages - messageCount;
      } else {
        draftState.total_messages = messageCount;
      }
    });
    internalConversation$.next(state);
  },
  updateActiveWindow: () => {
    const state = produce(internalConversation$.value, (draftState) => {
      draftState.active_window = undefined;
    });
    internalConversation$.next(state);
  }
};
/**
 * Observable Usage everywhere.
 */
export default function useInternalConversationStore(): InternalChatState {
  return useObservable<InternalChatState>(internalConversation$, internalConversation$.value);
}
